import {
  Alert,
  Button,
  Col,
  Descriptions,
  DescriptionsProps,
  Empty,
  Form,
  Row,
  Tag,
} from "antd";
import styled from "styled-components";
import { GrStatusUnknown } from "react-icons/gr";
import { TrancheDetailsModel } from "../../../domaine/projectModel";
import Title from "antd/es/typography/Title";
import {
  MapContainer,
  Marker,
  Polygon,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { useEffect, useState } from "react";
import L from "leaflet";
import svg_icon from "../../../assets/icons8-location.gif";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import NestedPieChart from "./NestedPieCharts";
import { AppDispatch, RootState } from "../../../store/store";
import { useDispatch } from "react-redux";
import { fetchVisitesByTranche } from "../../../store/slices/visiteSlice";
import VisiteHistorique from "./HistoriqueVisites";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { TrancheEditDrawer } from "./TrancheEditDrawer";
import { idText } from "typescript";

interface Props {
  selectedTranche: any;
  projectDetails: any;
  setSelectedTranche: any;
}

export const TrancheDetails: React.FC<Props> = ({
  selectedTranche,
  setSelectedTranche,
  projectDetails,
}) => {
  const { visitesByTranche } = useSelector((state: RootState) => state.visites);
  const dispatch: AppDispatch = useDispatch();

  const optionComposantsColors: Record<string, string> = {
    appartements: "purple",
    villas: "green",
    "locaux commerciaux": "cyan",
    bureaux: "red",
  };

  const optionUsageColors: Record<string, string> = {
    residentiel: "green",
    professionel: "red",
    commerce: "cyan",
  };

  // Color mapping for programmation
  // const programmationColors: Record<string, string> = {
  //   studios: "purple",
  //   f2: "purple",
  //   f3: "purple",
  //   f4: "purple",
  //   f5: "purple",
  //   duplex: "purple",
  //   triplex: "purple",
  //   isolées: "green",
  //   jumelées: "green",
  //   enbande: "green",
  //   "centres commerciaux": "cyan",
  //   "pied d'immeuble": "cyan",
  //   showrooms: "cyan",
  //   coworking: "red",
  //   "salles de conférences": "red",
  //   "bureaux individuels": "red",
  //   "open space": "red",
  // };

  // const orderProgrammation = (programmation: string | null): string[] => {
  //   if (!programmation) {
  //     return []; // Handle null or empty value
  //   }

  //   const programmations = programmation.split(",").map((prog) => prog.trim()); // Split and trim

  //   // Sort based on the order of keys in programmationColors
  //   return programmations.sort((a, b) => {
  //     const indexA = Object.keys(programmationColors).indexOf(a);
  //     const indexB = Object.keys(programmationColors).indexOf(b);

  //     // If an item is not in programmationColors, it gets the lowest priority
  //     return (
  //       (indexA === -1 ? Infinity : indexA) -
  //       (indexB === -1 ? Infinity : indexB)
  //     );
  //   });
  // };

  // const renderOrderedProgrammationTags = (programmation: string | null) => {
  //   const orderedProgrammations = orderProgrammation(programmation);

  //   return orderedProgrammations.map((prog) => {
  //     const trimmedProg = prog.trim();
  //     const color = programmationColors[trimmedProg] || "default"; // Default color fallback
  //     return (
  //       <Tag key={trimmedProg} color={color} style={{ marginInlineEnd: 4 }}>
  //         {trimmedProg.charAt(0).toUpperCase() + trimmedProg.slice(1)}{" "}
  //         {/* Capitalize */}
  //       </Tag>
  //     );
  //   });
  // };

  const renderProjectComponents = (composants: string | null) => {
    if (!composants) {
      return <span>Aucune information disponible</span>; // Handle null or empty value
    }
    const types = composants.split(","); // Split the comma-separated string
    return types.map((type) => {
      const trimmedType = type.trim(); // Ensure no leading/trailing spaces
      return (
        <Tag
          key={trimmedType}
          color={optionComposantsColors[trimmedType] || "default"} // Default color fallback
          style={{ marginInlineEnd: 4 }}
        >
          {trimmedType.charAt(0).toUpperCase() + trimmedType.slice(1)}{" "}
          {/* Capitalize */}
        </Tag>
      );
    });
  };

  const renderUsageComponents = (usage: string | null) => {
    if (!usage) {
      return <span>Aucune information disponible</span>; // Handle null or empty value
    }
    const types = usage.split(","); // Split the comma-separated string
    return types.map((type) => {
      const trimmedType = type.trim(); // Ensure no leading/trailing spaces
      return (
        <Tag
          key={trimmedType}
          color={optionUsageColors[trimmedType] || "default"} // Default color fallback
          style={{ marginInlineEnd: 4 }}
        >
          {trimmedType.charAt(0).toUpperCase() + trimmedType.slice(1)}{" "}
          {/* Capitalize */}
        </Tag>
      );
    });
  };
  // Helper function to render Yes/No for boolean values
  const renderBoolean = (value: boolean | null | undefined) => {
    if (value === null || value === undefined) return "Non défini";
    return value ? (
      <CheckCircleOutlined style={{ color: "green" }} />
    ) : (
      <CloseCircleOutlined style={{ color: "red" }} />
    );
  };

  // Helper function to render number with fallback
  const renderNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "-";
    return value.toString();
  };
  // Function to render the map with polygon
  // Custom hook to track zoom level
  // Function to render the map with polygon
  const [zoomLevel, setZoomLevel] = useState(17);

  // Create a MapControl component to handle the useMap hook
  function MapControls({
    coordinates,
    center,
  }: {
    coordinates: [number, number][];
    center: [number, number];
  }) {
    const map = useMap(); // useMap hook is now safely inside a component rendered by MapContainer

    // Tracker for zoom level
    useMapEvents({
      zoomend: (e) => setZoomLevel(e.target.getZoom()),
    });

    // Calculate polygon bounds
    const polygonBounds = L.polygon(coordinates).getBounds();

    return (
      <>
        {zoomLevel >= 16 ? (
          <Polygon
            positions={coordinates}
            pathOptions={{
              color: "#ff4d4f", // Modern red color
              fillColor: "#ff7875", // Softer fill color
              fillOpacity: 0.6, // Slight transparency for a softer look
              weight: 3, // Thinner border
              dashArray: "6, 6", // Dashed border for a sleek effect
            }}
            eventHandlers={{
              mouseover: (e) => {
                e.target.setStyle({
                  color: "#d9363e", // Darker red on hover
                  fillOpacity: 0.8, // Stronger opacity
                });
              },
              mouseout: (e) => {
                e.target.setStyle({
                  color: "#ff4d4f", // Reset to original color
                  fillOpacity: 0.6,
                });
              },
            }}
          ></Polygon>
        ) : (
          <Marker
            position={center}
            icon={
              new L.Icon({
                iconUrl: svg_icon,
                iconSize: [32, 32],
                iconAnchor: [16, 32],
              })
            }
            eventHandlers={{
              click: () => {
                map.fitBounds(polygonBounds, { padding: [50, 50] }); // Zoom to polygon
              },
            }}
          ></Marker>
        )}
      </>
    );
  }

  const renderTrancheMap = (tranche: any) => {
    if (
      !tranche.fk_adresse?.coordonnees_polygone ||
      tranche.fk_adresse.coordonnees_polygone.length < 3
    ) {
      return (
        <div>Aucune coordonnée de polygone disponible pour cette tranche</div>
      );
    }

    const coordinates = tranche.fk_adresse.coordonnees_polygone;
    const center: [number, number] = [
      tranche.fk_adresse.coordonnees_latitude,
      tranche.fk_adresse.coordonnees_longitude,
    ];

    return (
      <>
        <Title level={5}>Localisation</Title>
        <p>
          <span style={{ fontWeight: "bold" }}>Adresse :</span>{" "}
          {tranche.fk_adresse.adresse}
        </p>
        <p>
          <span style={{ fontWeight: "bold" }}>Arrondissement / Commune :</span>{" "}
          {tranche.fk_adresse.fk_commune}
        </p>
        <MapContainer
          center={center}
          zoom={zoomLevel}
          style={{
            height: 500,
            width: "100%",
            borderRadius: "10px",
            marginTop: "10px",
          }}
        >
          <TileLayer
            attribution="Google Maps"
            url="https://www.google.cn/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}"
          />
          <TileLayer
            attribution="OpenStreetMap/Carto"
            url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
          />

          {/* MapControls component uses hooks within MapContainer context */}
          <MapControls coordinates={coordinates} center={center} />
        </MapContainer>
      </>
    );
  };
  // Create tranche description items for a specific tranche
  const getTrancheDescriptionItems = (
    tranche: TrancheDetailsModel,
    realisationType?: string
  ): DescriptionsProps["items"] => {
    const items: DescriptionsProps["items"] = [];

    // Conditionally add the "Nom" field if realisationType is not "en totalité"
    if (realisationType !== "En totalité") {
      items.push({ label: "Nom", children: tranche.name });
    }

    // Add the rest of the fields
    items.push(
      {
        label: "Surface foncière",
        children: `${tranche.surface_fonciere} m²`,
      },
      {
        label: "Surface construite",
        children: `${tranche.surface_construite} m²`,
      },
      {
        label: "Surface vendable",
        children: `${tranche.surface_vendable} m²`,
      },
      {
        label: "Date de permis de construire",
        children: tranche.date_de_permis_de_construire,
      },
      {
        label: "Date permis d'habiter",
        children: tranche.date_permis_d_habiter || "Non définie",
      },
      {
        label: "Nombre total d'unités",
        children:
          tranche.is_visited  ? (
            renderNumber(tranche.nbr_unites_total)
          ) : (
            <GrStatusUnknown />
          ),
      },
      {
        label: "Type de standing",
        children:
          tranche.is_visited  ? (
            tranche.type_de_standing
          ) : (
            <GrStatusUnknown />
          ),
      },
      {
        label: "Phase du projet",
        children:
          tranche.is_visited  ? (
            tranche.phase_du_projet
          ) : (
            <GrStatusUnknown />
          ),
      },
      {
        label: "État d'avancement",
        children:
          tranche.is_visited  ? (
            tranche.etat_avancement
          ) : (
            <GrStatusUnknown />
          ),
      },
      {
        label: "Composants",
        children:
          tranche.is_visited  ? (
            renderProjectComponents(tranche.composants)
          ) : (
            <GrStatusUnknown />
          ),
        span: 2,
      },
      {
        label: "Usage",
        children:
          tranche.is_visited  ? (
            renderUsageComponents(tranche.usage)
          ) : (
            <GrStatusUnknown />
          ),
        span: 2,
      }
    );

    return items;
  };

  // Create tranche characteristics items
  const getTrancheCharacteristicsItems = (
    tranche: TrancheDetailsModel
  ): DescriptionsProps["items"] => [
    {
      label: "Nombre d'appartements",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_unites_appartements)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre de villas",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_unites_villas)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre de bureaux",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_unites_bureaux)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre de locaux commerciaux",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_unites_lc)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre d'étages",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_etages)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Clôturée",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.cloturee)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Ascenseur",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.ascenseur)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre d'ascenseurs",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_d_ascenseurs)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Sous-sol",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.sous_sol)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre de sous-sols",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_de_sous_sol)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Parking appartements",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.parking_appartements)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre de places (appartements)",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_de_place_de_parking_appartements)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Parking villas",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.parking_villas)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Nombre de places (villas)",
      children:
        tranche.is_visited  ? (
          renderNumber(tranche.nbr_de_place_de_parking_villas)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Espace vert",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.espace_vert)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Piscine",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.piscine)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Terrain de sport",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.terrain_de_sport)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Salle de sport",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.salle_de_sport)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Crèche",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.creche)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Validé",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.is_valide)
        ) : (
          <GrStatusUnknown />
        ),
    },
    {
      label: "Visité",
      children:
        tranche.is_visited  ? (
          renderBoolean(tranche.is_visited )
        ) : (
          <GrStatusUnknown />
        ),
    },
  ];

  useEffect(() => {
    if (projectDetails.type_realisation === "En totalité") {
      setSelectedTranche(projectDetails.tranches[0].id);
    }
    if (selectedTranche) {
      const tranche = projectDetails.tranches.find(
        (t: any) => t.id === selectedTranche
      );
      if (tranche) {
        dispatch(fetchVisitesByTranche(selectedTranche));
      }
    }
    
  }, [selectedTranche]);

   // State for drawer visibility
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    
    const [form] = Form.useForm();
  
    // Function to open the edit drawer
    const openEditDrawer = (tranche: TrancheDetailsModel) => {
      
      if (tranche) {
        form.setFieldsValue({
          id: tranche.id,
          name: tranche.name,
          date_de_permis_de_construire: dayjs(
            tranche.date_de_permis_de_construire,
            "DD/MM/YYYY"
          ).isValid()
            ? dayjs(tranche.date_de_permis_de_construire, "DD/MM/YYYY")
            : null,
          date_permis_d_habiter: dayjs(
            tranche.date_permis_d_habiter,
            "DD/MM/YYYY"
          ).isValid()
            ? dayjs(tranche.date_permis_d_habiter, "DD/MM/YYYY")
            : null,
          surface_fonciere: tranche.surface_fonciere,
          surface_construite: tranche.surface_construite,
          surface_vendable: tranche.surface_vendable,
          phase_du_projet: tranche.phase_du_projet,
          etat_avancement: tranche.etat_avancement,
        });
      }
  
      setEditDrawerVisible(true);
    };

  return (
    selectedTranche && (
      <>
        {projectDetails.tranches
          .filter((t: any) => t.id === selectedTranche)
          .map((tranche: TrancheDetailsModel) => (
            <div key={tranche.id}>
              <TrancheEditDrawer
                form={form}
                setEditDrawerVisible={setEditDrawerVisible}
                editDrawerVisible={editDrawerVisible}
              />
              <Row justify={"end"} style={{ width: "100%" }}>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    openEditDrawer(tranche);
                  }}>Modifier</Button>
              </Row>
              {projectDetails.type_realisation === "Par tranches" && (
                <Title level={4} style={{ marginTop: "20px" }}>
                  Détails de la tranche: {tranche.name}
                </Title>
              )}
              {!tranche.suivi_initial && (
                <Alert
                  showIcon
                  message="Aucun suivi à réaliser (commercialisation achevée avant les visites)"
                  type="info"
                />
              )}

              <Row
                gutter={16}
                style={{ width: "100%" }}
                justify={"space-between"}
              >
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Descriptions
                    title="Informations générales"
                    
                    layout="vertical"
                    size="small"
                    column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                    items={getTrancheDescriptionItems(
                      tranche,
                      projectDetails.type_realisation
                    )}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Title level={5}>
                    Répartition des composants du projet et des programmations
                  </Title>
                  {tranche.is_visited  ? (
                    <NestedPieChart visite={visitesByTranche[0]} />
                  ) : (
                    <Empty style={{ marginTop: "70px" }} />
                  )}
                </Col>
              </Row>
              <Row
                gutter={16}
                style={{ width: "100%", marginTop: "20px" }}
                justify={"space-between"}
              >
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Descriptions
                    title="Caractéristiques"
                    layout="vertical"
                    size="small"
                    column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                    items={getTrancheCharacteristicsItems(tranche)}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                  {renderTrancheMap(tranche)}
                </Col>
              </Row>
              <Row gutter={16} style={{ width: "100%", marginTop: "20px" }}>
                {tranche.suivi_initial && (
                  <VisiteHistorique visites={visitesByTranche} />
                )}
                
              </Row>
            </div>
          ))}
      </>
    )
  );
};
